<!--
@component
This component displays an image located either on the Pangea GDB or at a custom URL. The image presentation is based on the `<img>` element and utilizes `srcset` for responsive image selection.

#### General properties
@prop {string} url - Specifies the URL of the image.
@prop {string} alt - Specifies the alternative text for the image, used for accessibility.
@prop {ArrayLike<number>} thumbhash - Specifies the thumbhash data which is used for creating the placeholder.
@prop {ContainerWidth} columnWidth - Specifies the preferred width of the image's parent container (responsive design column).
@prop {loading} loading - Specifies the loading strategy of the image. By default, it is set to `lazy`.
@prop {number} imgWidth - Specifies the width of the image. By default, it is set to 700 pixels.
-->
<script lang="ts">
	// IMPORTS
	import { assets } from '$app/paths';
	import {
		thumbHashToDataURL,
		thumbHashToApproximateAspectRatio,
	} from 'thumbhash';
	import { columns } from '$stores/mq';
	import { isGdb, getGdbSrcSet, getGdbDefaultSrc } from '$utils/gdb';
	import { ScrollTriggerInView } from '$actions/ScrollTriggerInView';
	import { createEventDispatcher } from 'svelte';
	import mediaQueries from '$stores/mq';

	// TYPES
	import type { ContainerWidth } from '$types/ContainerWidth';

	// PROPS
	export let url!: string;
	export let alt: string;
	export let thumbhash!: ArrayLike<number>;
	5;
	export let columnWidth: ContainerWidth | null = null;
	export let imgWidth: number = 500;
	export let loading: 'lazy' | 'eager' = 'lazy';

	// VARIABLES
	const dispatch = createEventDispatcher();
	let imgSrcThumb: string = thumbHashToDataURL(thumbhash);
	let imgAspectRatio: number = thumbHashToApproximateAspectRatio(thumbhash);
	const sizes: Array<number> = [columns.narrow, columns.normal, columns.wide];
	let srcAttr: string, srcsetAttr: string, sizesAttr: string;
	let isShort = $mediaQueries.short;

	// LOGIC
	function getImgWidth() {
		const widthMap = {
			narrow: columns.narrow,
			normal: columns.normal,
			wide: columns.wide,
			'full-width': columns['full-width'],
		};

		let width = widthMap[columnWidth] || imgWidth || columns.normal;

		if (isShort) {
			width /= 2; // Halve the image width if isShort is true
		}

		return (imgWidth = width);
	}

	function localImgSrcSetString() {
		// Returns a srcset string for local images
		const [cleanUrl, ext] = url.split('.');
		const srcset = sizes.map((size) => {
			return `${assets}/images/${cleanUrl}_w${size}.${ext} ${size}w`;
		});
		return srcset.join(', ');
	}

	function getLocalDefaultSrc() {
		// Returns a default src for local images
		const [cleanUrl, ext] = url.split('.');
		return `${assets}/images/${cleanUrl}_w${getImgWidth()}.${ext}`;
	}

	function getSrcAttr(url: string) {
		if (isGdb(url)) {
			return getGdbDefaultSrc(url, getImgWidth());
		} else {
			return getLocalDefaultSrc();
		}
	}

	function getSrcSetAttr(url: string) {
		if (isGdb(url)) {
			return getGdbSrcSet(url, sizes);
		} else {
			return localImgSrcSetString();
		}
	}

	function loadImg(el: HTMLImageElement): void {
		// Function to load src, sizes, and srcset from data attributes
		el.src = srcAttr;
		el.srcset = srcsetAttr;
		el.sizes = sizesAttr;
		dispatch('imgLoad', { message: 'Image loading completed.' });
	}

	function setUpImg(el: HTMLImageElement, url: string) {
		// 1. Set initial size, and placeholder
		el.src = imgSrcThumb;
		el.width = getImgWidth();
		el.height = getImgWidth() / imgAspectRatio;

		// 2. Retrieve permanent values
		srcAttr = getSrcAttr(url);
		srcsetAttr = getSrcSetAttr(url);
		sizesAttr = 'auto';

		dispatch('imgMounted', { message: 'Image component has mounted.' });
	}
</script>

<div class="img-wrapper">
	<img
		use:setUpImg="{url}"
		use:ScrollTriggerInView="{loadImg}"
		{alt}
		{loading}
		decoding="async"
	/>
</div>

<style lang="scss">
	img {
		display: block;
		margin: 0 auto;
		aspect-ratio: attr(width) / attr(height);
		max-width: 100%;
		height: auto;
	}
</style>
